/**
 * One WebRTC connection to one other player. Audio only, and nothing else —
 * no data channel, no video, no statistics.
 *
 * The mesh is deliberately dumb: at `MAX_PLAYERS` = 16, scoped to a single
 * floor, scoped again to `VOICE_FAR`, the number of live connections a client
 * holds is almost always zero and occasionally one or two. An SFU would be more
 * machinery than the whole rest of the multiplayer put together.
 *
 * Signalling rides the game socket as opaque `signal` messages that the server
 * relays without looking at them. The blobs below are the only thing either end
 * needs to agree on, so the server can stay a relay forever.
 *
 * Glare — both ends offering at once, which happens constantly when two people
 * walk into range of each other simultaneously — is handled with the standard
 * perfect-negotiation pattern. The polite side is whichever peer id sorts
 * lower, so both ends derive the same answer from information they already have
 * and there is nothing to coordinate.
 *
 * Every failure here is silent and ends in the same place: no remote stream,
 * and `mutter.ts` speaks for that peer instead.
 */

import type { PeerId } from '../net/protocol';

/**
 * The wire format for signalling, wrapped so a future change is detectable.
 * Deliberately terse; this is inside a `signal` message's opaque `data`.
 */
type VoiceSignal =
  | { v: 1; k: 'desc'; d: RTCSessionDescriptionInit }
  | { v: 1; k: 'ice'; c: RTCIceCandidateInit };

/**
 * Public STUN. Without one, two players behind different routers can only
 * connect on a LAN; with it, everything short of a symmetric NAT works. There
 * is no TURN, so a small number of connections will simply never come up —
 * those peers mutter, which is a soft failure rather than a broken game.
 */
export const DEFAULT_ICE: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

export interface LinkHooks {
  /** Hand a signalling blob to the transport. */
  send(data: unknown): void;
  /** A remote audio stream arrived, or went away. */
  onStream(stream: MediaStream | null): void;
  /** The connection is dead and the link should be dropped. */
  onFailed(): void;
}

/** True when this browser can do WebRTC at all. */
export function webrtcSupported(): boolean {
  return typeof RTCPeerConnection !== 'undefined';
}

/** Both ends compute this from the two ids and agree without a round trip. */
export function isPolite(self: PeerId, other: PeerId): boolean {
  return self < other;
}

export class VoiceLink {
  private pc: RTCPeerConnection | null = null;
  private sender: RTCRtpSender | null = null;
  private makingOffer = false;
  private ignoreOffer = false;
  private closed = false;
  /** Candidates that arrived before there was a remote description to hang them on. */
  private readonly earlyIce: RTCIceCandidateInit[] = [];
  private haveRemote = false;

  constructor(
    private readonly polite: boolean,
    private readonly hooks: LinkHooks,
    iceServers: RTCIceServer[] = DEFAULT_ICE,
  ) {
    if (!webrtcSupported()) return;
    let pc: RTCPeerConnection;
    try {
      pc = new RTCPeerConnection({ iceServers });
    } catch {
      return;
    }
    this.pc = pc;

    pc.onnegotiationneeded = () => {
      void this.negotiate();
    };

    pc.onicecandidate = (e) => {
      // The end-of-candidates null is not worth a message; both ends time out
      // on their own and the connection works without it.
      if (e.candidate) this.emit({ v: 1, k: 'ice', c: e.candidate.toJSON() });
    };

    pc.ontrack = (e) => {
      if (this.closed) return;
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.hooks.onStream(stream);
    };

    pc.onconnectionstatechange = () => {
      if (this.closed) return;
      const s = pc.connectionState;
      // 'disconnected' recovers on its own more often than not, so only a hard
      // failure tears the link down. The chat layer rebuilds it if the peer is
      // still in range.
      if (s === 'failed' || s === 'closed') this.hooks.onFailed();
    };
  }

  /** False when this browser could not give us a connection at all. */
  get ok(): boolean {
    return this.pc !== null;
  }

  /**
   * Publish (or withdraw) the local microphone. Called when the player grants
   * the microphone long after the link came up, which is the normal case: the
   * prompt only appears once somebody is already standing next to them.
   */
  setLocalStream(stream: MediaStream | null): void {
    const pc = this.pc;
    if (!pc || this.closed) return;
    const track = stream?.getAudioTracks()[0] ?? null;
    try {
      if (this.sender) {
        void this.sender.replaceTrack(track);
      } else if (track && stream) {
        this.sender = pc.addTrack(track, stream);
      }
    } catch {
      /* the connection is going away; nothing to publish onto */
    }
  }

  /** An inbound signalling blob, straight off the socket. */
  accept(data: unknown): void {
    const msg = data as Partial<VoiceSignal> | null;
    if (!msg || msg.v !== 1) return;
    if (msg.k === 'desc' && msg.d) void this.onDescription(msg.d);
    else if (msg.k === 'ice' && msg.c) void this.onCandidate(msg.c);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    const pc = this.pc;
    this.pc = null;
    this.sender = null;
    if (!pc) return;
    pc.onnegotiationneeded = null;
    pc.onicecandidate = null;
    pc.ontrack = null;
    pc.onconnectionstatechange = null;
    try {
      pc.close();
    } catch {
      /* already closed */
    }
  }

  /* ---------------------------------------------------------------- *
   * Negotiation
   * ---------------------------------------------------------------- */

  private async negotiate(): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;
    try {
      this.makingOffer = true;
      const offer = await pc.createOffer();
      if (this.closed || pc.signalingState !== 'stable') return;
      await pc.setLocalDescription(offer);
      if (pc.localDescription) this.emit({ v: 1, k: 'desc', d: pc.localDescription.toJSON() });
    } catch {
      /* a renegotiation that fails leaves the previous state working */
    } finally {
      this.makingOffer = false;
    }
  }

  private async onDescription(d: RTCSessionDescriptionInit): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;

    const collision = d.type === 'offer' && (this.makingOffer || pc.signalingState !== 'stable');
    // The impolite side wins a collision by pretending the other offer never
    // arrived; the polite side gives up its own.
    this.ignoreOffer = !this.polite && collision;
    if (this.ignoreOffer) return;

    try {
      if (collision) {
        // Rollback is the correct move and is not implemented everywhere. When
        // it is missing, taking the remote offer straight is usually still
        // accepted, and when it is not the link fails into muttering.
        try {
          await pc.setLocalDescription({ type: 'rollback' });
        } catch {
          /* no rollback on this engine */
        }
      }
      await pc.setRemoteDescription(d);
      this.haveRemote = true;
      await this.flushIce();
      if (d.type === 'offer') {
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        if (pc.localDescription) this.emit({ v: 1, k: 'desc', d: pc.localDescription.toJSON() });
      }
    } catch {
      /* malformed or out of order; the next negotiation will try again */
    }
  }

  private async onCandidate(c: RTCIceCandidateInit): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;
    if (!this.haveRemote) {
      // Bounded, so a peer that never answers cannot grow this forever.
      if (this.earlyIce.length < 64) this.earlyIce.push(c);
      return;
    }
    try {
      await pc.addIceCandidate(c);
    } catch {
      // Expected while an offer we chose to ignore is still in flight.
    }
  }

  private async flushIce(): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    const queued = this.earlyIce.splice(0, this.earlyIce.length);
    for (const c of queued) {
      try {
        await pc.addIceCandidate(c);
      } catch {
        /* stale candidate */
      }
    }
  }

  private emit(s: VoiceSignal): void {
    if (this.closed) return;
    this.hooks.send(s);
  }
}
